'use client';

import { useEffect } from 'react';

/**
 * 全局 Tab 键导航管理器
 * 确保用户首次按 Tab 键时直接定位到右侧悬浮按钮
 * 但避免干扰弹窗/抽屉内的焦点循环
 */
export function TabNavigationManager() {
  useEffect(() => {
    let hasInteracted = false;

    const handleFirstTab = (e: KeyboardEvent) => {
      // 只处理 Tab 键
      if (e.key !== 'Tab') return;

      // 如果已经有过交互，移除监听器
      if (hasInteracted) {
        removeEventListener();
        return;
      }

      /**
       * 检查是否有模态框/弹窗打开
       * 如果有,跳过全局首 Tab 逻辑
       */
      const hasOpenModal = document.querySelector('[aria-modal="true"]');
      if (hasOpenModal) {
        removeEventListener();
        return;
      }

      /**
       * 检查当前焦点元素是否在弹层内
       * 简单检查: 是否在有固定/绝对定位且高 z-index 的元素内
       */
      const activeElement = document.activeElement;
      const isInOverlay = activeElement
        ? (() => {
            const element = activeElement as HTMLElement;

            // 检查元素本身或其最近的固定定位父元素
            let current = element;
            let iterations = 0;
            const maxIterations = 5; // 限制向上查找层级

            while (current && iterations < maxIterations) {
              const style = window.getComputedStyle(current);
              const position = style.position;
              const zIndex = style.zIndex;

              if (
                (position === 'fixed' || position === 'absolute') &&
                zIndex !== 'auto' &&
                parseInt(zIndex) > 100
              ) {
                return true;
              }

              current = current.parentElement;
              iterations++;
            }

            return false;
          })()
        : false;

      if (isInOverlay) {
        removeEventListener();
        return;
      }

      // 标记已交互
      hasInteracted = true;

      // 查找右侧悬浮按钮的第一个可聚焦元素
      const rightBottomFloating = document.querySelector('[data-testid="right-bottom-floating"]');
      if (rightBottomFloating) {
        const firstFocusable = rightBottomFloating.querySelector<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );

        if (firstFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }

      // 一次后移除监听器
      removeEventListener();
    };

    const removeEventListener = () => {
      document.removeEventListener('keydown', handleFirstTab);
    };

    // 监听首次 Tab 键
    document.addEventListener('keydown', handleFirstTab);

    // 清理
    return removeEventListener;
  }, []);

  return null;
}
